import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  ClipboardList, 
  ExternalLink, 
  Copy, 
  Trash2, 
  Edit, 
  Eye, 
  FileDown, 
  MoreVertical,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
  FileText,
  Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function FormsPage() {
  const { api, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create form dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormDescription, setNewFormDescription] = useState('');
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Submissions dialog
  const [showSubmissionsDialog, setShowSubmissionsDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [submissionsPage, setSubmissionsPage] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  
  // Delete submission
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter === 'open') params.append('status', 'open');
      if (statusFilter === 'closed') params.append('status', 'closed');
      
      const response = await api.get(`/forms?${params}`);
      setForms(response.data.forms || []);
    } catch (error) {
      toast.error('Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleCreateForm = async () => {
    if (!newFormName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/forms', {
        name: newFormName.trim(),
        description: newFormDescription.trim(),
        fields: [],
        allow_multiple_submissions: allowMultiple
      });
      
      toast.success('Form created successfully!');
      setShowCreateDialog(false);
      setNewFormName('');
      setNewFormDescription('');
      setAllowMultiple(true);
      
      // Navigate to form builder
      navigate(`/form-builder/${response.data.form_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (form) => {
    try {
      await api.put(`/forms/${form.id}`, {
        is_open: !form.is_open
      });
      toast.success(`Form ${form.is_open ? 'closed' : 'opened'} successfully`);
      fetchForms();
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const handleDeleteClick = (form) => {
    setFormToDelete(form);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!formToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/forms/${formToDelete.id}`);
      toast.success('Form deleted successfully');
      setShowDeleteDialog(false);
      setFormToDelete(null);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    } finally {
      setDeleting(false);
    }
  };

  const copyFormLink = (formId) => {
    const link = `${window.location.origin}/public-form/${formId}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => toast.success('Form link copied to clipboard!'))
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = link;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Form link copied to clipboard!');
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Form link copied to clipboard!');
    }
  };

  const viewSubmissions = async (form) => {
    setSelectedForm(form);
    setShowSubmissionsDialog(true);
    setLoadingSubmissions(true);
    setSubmissionsPage(1);
    
    try {
      const response = await api.get(`/forms/${form.id}/submissions?page=1&limit=50`);
      setSubmissions(response.data.submissions || []);
      setTotalSubmissions(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!selectedForm || !submissionId) return;
    
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }
    
    setDeletingSubmissionId(submissionId);
    try {
      await api.delete(`/forms/${selectedForm.id}/submissions/${submissionId}`);
      
      // Remove from local state
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      setTotalSubmissions(prev => prev - 1);
      
      // Update form submission count in forms list
      setForms(prev => prev.map(f => 
        f.id === selectedForm.id 
          ? { ...f, submission_count: (f.submission_count || 1) - 1 }
          : f
      ));
      
      toast.success('Submission deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submission');
    } finally {
      setDeletingSubmissionId(null);
    }
  };

  const exportSubmissions = async (form, format) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} export...`);
      
      const response = await api.get(`/forms/${form.id}/export?format=${format}`, {
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for large exports
      });
      
      // Check if response is actually an error (JSON) disguised as blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Export failed');
      }
      
      // Get correct MIME type based on format
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${form.name.replace(/\s+/g, '_')}_submissions.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      // Try to get detailed error message
      let errorMsg = 'Failed to export submissions';
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMsg = errorData.detail || errorMsg;
          } catch (e) {
            // Couldn't parse error blob
          }
        } else {
          errorMsg = error.response.data.detail || errorMsg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    }
  };

  // Filter forms by search query
  const filteredForms = forms.filter(form => 
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="forms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">Dynamic Forms</h1>
          <p className="text-slate-500 mt-1">Create and manage custom forms with member data integration</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)} 
          className="bg-navy hover:bg-navy/90"
          data-testid="create-form-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-navy" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{forms.length}</p>
                <p className="text-sm text-slate-500">Total Forms</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">
                  {forms.filter(f => f.is_open).length}
                </p>
                <p className="text-sm text-slate-500">Open Forms</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {forms.filter(f => !f.is_open).length}
                </p>
                <p className="text-sm text-slate-500">Closed Forms</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">
                  {forms.reduce((sum, f) => sum + (f.submission_count || 0), 0)}
                </p>
                <p className="text-sm text-slate-500">Total Submissions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search forms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-forms-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open Only</SelectItem>
            <SelectItem value="closed">Closed Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Forms List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredForms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No forms found</h3>
            <p className="text-slate-500 mt-1">
              {searchQuery ? 'Try adjusting your search' : 'Create your first form to get started'}
            </p>
            {!searchQuery && (
              <Button 
                onClick={() => setShowCreateDialog(true)} 
                className="mt-4 bg-navy hover:bg-navy/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredForms.map((form) => (
            <Card key={form.id} className="hover:shadow-md transition-shadow" data-testid={`form-card-${form.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-800 truncate">{form.name}</h3>
                      <Badge className={form.is_open ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {form.is_open ? 'Open' : 'Closed'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-1">{form.description || 'No description'}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
                      <span>{form.fields?.length || 0} fields</span>
                      <span>{form.submission_count || 0} submissions</span>
                      <span>Created: {formatDate(form.created_at)}</span>
                      {form.allow_multiple_submissions === false && (
                        <Badge variant="outline" className="text-xs">One submission per ITS</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyFormLink(form.id)}
                      data-testid={`copy-link-${form.id}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/form-results/${form.id}`)}
                      data-testid={`view-results-${form.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Results
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`form-menu-${form.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/form-builder/${form.id}`)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Form
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/public-form/${form.id}`, '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Preview Form
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleToggleStatus(form)}>
                          {form.is_open ? (
                            <>
                              <XCircle className="w-4 h-4 mr-2" />
                              Close Form
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Open Form
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => exportSubmissions(form, 'excel')}>
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Export Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => exportSubmissions(form, 'pdf')}>
                          <FileText className="w-4 h-4 mr-2" />
                          Export PDF
                        </DropdownMenuItem>
                        {isSuperAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(form)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Form
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Form Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-form-dialog">
          <DialogHeader>
            <DialogTitle>Create New Form</DialogTitle>
            <DialogDescription>
              Set up your form basics. You can add fields in the next step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="formName">Form Name *</Label>
              <Input
                id="formName"
                placeholder="e.g., Event Registration, Survey..."
                value={newFormName}
                onChange={(e) => setNewFormName(e.target.value)}
                data-testid="form-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="formDescription">Description</Label>
              <Textarea
                id="formDescription"
                placeholder="Brief description of the form..."
                value={newFormDescription}
                onChange={(e) => setNewFormDescription(e.target.value)}
                rows={3}
                data-testid="form-description-input"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="allowMultiple">Allow Multiple Submissions</Label>
                <p className="text-xs text-slate-500">If off, one submission per ITS number</p>
              </div>
              <Switch
                id="allowMultiple"
                checked={allowMultiple}
                onCheckedChange={setAllowMultiple}
                data-testid="allow-multiple-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateForm} 
              disabled={creating}
              className="bg-navy hover:bg-navy/90"
              data-testid="confirm-create-form-btn"
            >
              {creating ? 'Creating...' : 'Create & Add Fields'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md" data-testid="delete-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Form</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{formToDelete?.name}"? 
              This will also delete all {formToDelete?.submission_count || 0} submissions and uploaded files.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-form-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions Dialog */}
      <Dialog open={showSubmissionsDialog} onOpenChange={setShowSubmissionsDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="submissions-dialog">
          <DialogHeader>
            <DialogTitle>Submissions: {selectedForm?.name}</DialogTitle>
            <DialogDescription>
              {totalSubmissions} total submissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {loadingSubmissions ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-4 border-navy border-t-transparent rounded-full animate-spin" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                No submissions yet
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map((sub, idx) => (
                  <Card key={sub.id} className="bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                          {sub.its_id && (
                            <Badge variant="outline">{sub.its_id}</Badge>
                          )}
                          {sub.member_data?.full_name && (
                            <span className="text-sm font-medium">{sub.member_data.full_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            {formatDate(sub.submitted_at)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteSubmission(sub.id)}
                            disabled={deletingSubmissionId === sub.id}
                            data-testid={`delete-submission-${sub.id}`}
                          >
                            {deletingSubmissionId === sub.id ? (
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {Object.entries(sub.responses || {}).slice(0, 6).map(([key, value]) => {
                          // Find field label
                          const field = selectedForm?.fields?.find(f => f.id === key);
                          const label = field?.label || key;
                          const displayValue = Array.isArray(value) ? value.join(', ') : String(value || '');
                          
                          return (
                            <div key={key}>
                              <span className="text-slate-500">{label}: </span>
                              <span className="font-medium">{displayValue.substring(0, 50)}{displayValue.length > 50 ? '...' : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => exportSubmissions(selectedForm, 'excel')}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button variant="outline" onClick={() => exportSubmissions(selectedForm, 'pdf')}>
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
